import {
  faCheck,
  faExternalLink,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import MissingExpenseApprovalModal from 'components/DetailedCase/MissingExpenseApprovalModal';
import Modal from 'components/Modal';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import React, { FC, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import useAutoapproveAssignmentsBulkOperations from './useAutoapproveAssignmentsBulkOperations';
import { useCaseGetAssignmentOverlapsFromIds } from 'api/case/case';
import { AssignmentExtendedModel, AssignmentViewModel } from 'api/model';
import TextButton from 'components/inputs/TextButton';
import { useNavigate } from 'react-router-dom';
import { formatTime } from 'utils/date-helpers';
import AuthorizedButton from 'components/inputs/AuthorizedButton';
import Roles from 'constants/Roles';

const Wrapper = styled.div``;

interface Props {
  bulkOperations: ReturnType<typeof useAutoapproveAssignmentsBulkOperations>;
  handleAssignmentItemClick?(model: AssignmentExtendedModel): void;
}

const AutoApproveBulkApproval: FC<Props> = ({
  bulkOperations,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleAssignmentItemClick,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showApproveModal, setShowApproveModal] = useState(false);

  // Uppdrag i urvalet som INTE är direkt godkännbara (avvikelser eller ej
  // kontrollerade) — de kan väljas via "Välj alla på sidan" och godkänns då
  // ändå, så bekräftelsemodalen ska varna tydligt.
  const notAutoApprovableSelectedCount = Object.values(
    bulkOperations.selectedAssignments
  ).filter((a) => a.result?.autoApprove !== true).length;
  const [overlapsDict, setOverlapsDict] = useState<
    Record<string, AssignmentViewModel[]>
  >({});

  const overlapsCall = useCaseGetAssignmentOverlapsFromIds();

  useEffect(() => {
    const updateOverlapsDict = async () => {
      if (bulkOperations.selectedAssignmentIds.size > 0) {
        const result = await overlapsCall.mutateAsync({
          data: Array.from(bulkOperations.selectedAssignmentIds),
        });

        setOverlapsDict(result);
      } else {
        setOverlapsDict({});
      }
    };
    updateOverlapsDict();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkOperations.selectedAssignmentIds]);

  // const [missingExpenses, setMissingExpenses] = useState<MissingExpenses[]>([]);

  // useEffect(() => {
  //   const checkExpectedExpenses = async () => {
  //     setMissingExpenses(await bulkOperations.checkExpectedExpenses());
  //   };
  //   checkExpectedExpenses();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [bulkOperations.selectedAssignmentIds, showApproveModal]);

  return (
    <Wrapper>
      <AuthorizedButton
        roles={[Roles.Ekonomi, Roles.SuperAdmin, Roles.Efterkontroll]}
        disabled={
          bulkOperations.selectedAssignmentIds.size === 0 ||
          bulkOperations.approveStatus === 'loading'
        }
        onClick={() => setShowApproveModal(true)}
      >
        {bulkOperations.approveStatus === 'loading' ? (
          <TransparentSpinner color={theme.colors.foreground.button} small />
        ) : (
          `Godkänn ${bulkOperations.selectedAssignmentIds.size || ''} uppdrag`
        )}
      </AuthorizedButton>

      {
        showApproveModal && (
          // (missingExpenses.length === 0 ? (
          <Modal
            buttons={[
              {
                label: 'Avbryt',
                onClick: () => setShowApproveModal(false),
              },
              {
                icon: (
                  <FontAwesomeIcon
                    icon={
                      bulkOperations.approveStatus === 'loading'
                        ? faSpinner
                        : faCheck
                    }
                  />
                ),
                label: `Godkänn${
                  bulkOperations.approveStatus === 'loading' ? 'er' : ''
                } ${bulkOperations.selectedAssignmentIds.size} uppdrag`,
                disabled:
                  Object.keys(overlapsDict).length > 0 ||
                  bulkOperations.approveStatus === 'loading',
                // loading: bulkOperations.approveStatus === 'loading',
                onClick: async () => {
                  await bulkOperations.approveSelectedAssignments();
                  setShowApproveModal(false);
                },
              },
            ]}
            onClose={() => setShowApproveModal(false)}
            title={`Godkänn ${bulkOperations.selectedAssignmentIds.size} uppdrag?`}
          >
            {Object.keys(overlapsDict).length > 0 ? (
              <>
                <p>Det finns överlappande uppdrag:</p>
                <ul>
                  {Object.entries(overlapsDict).map(([key, assignments]) =>
                    assignments.map((assignment) => (
                      <li key={key}>
                        <TextButton
                          onClick={() =>
                            navigate(
                              `/sok/${bulkOperations.selectedAssignments[Number(key)].assignment.case.caseID}`
                            )
                          }
                        >
                          {`${bulkOperations.selectedAssignments[Number(key)].assignment.case.registrationNumber} #${bulkOperations.selectedAssignments[Number(key)].assignment.assignment.assignmentID} `}
                          <FontAwesomeIcon icon={faExternalLink} />
                        </TextButton>
                        {bulkOperations.selectedAssignments[Number(key)]
                          .assignment.assignment.startTime &&
                          bulkOperations.selectedAssignments[Number(key)]
                            .assignment.assignment.endTime &&
                          `(${formatTime(bulkOperations.selectedAssignments[Number(key)].assignment.assignment.startTime)} – ${formatTime(bulkOperations.selectedAssignments[Number(key)].assignment.assignment.endTime)}) `}
                        &nbsp;och&nbsp;
                        <TextButton
                          onClick={() =>
                            navigate(`/sok/${assignment.case.caseID}`)
                          }
                        >
                          {`${assignment.case.registrationNumber} #${assignment.assignment.assignmentID} `}
                          <FontAwesomeIcon icon={faExternalLink} />
                        </TextButton>
                        {assignment.assignment.startTime &&
                          assignment.assignment.endTime &&
                          `(${formatTime(assignment.assignment.startTime)} - ${formatTime(assignment.assignment.endTime)})`}
                      </li>
                    ))
                  )}
                </ul>
              </>
            ) : (
              <>
                <p>Är du säker på att du vill godkänna markerade uppdrag?</p>
                {notAutoApprovableSelectedCount > 0 && (
                  <p style={{ color: theme.colors.foreground.error }}>
                    OBS: {notAutoApprovableSelectedCount} av de valda uppdragen
                    är inte direkt godkännbara (har avvikelser eller är ej
                    kontrollerade) och kommer att godkännas ändå.
                  </p>
                )}
              </>
            )}
          </Modal>
        )
        // ) : (
        //   <MissingExpenseApprovalModal
        //     handleAssignmentItemClick={(model: AssignmentExtendedModel) =>
        //       handleAssignmentItemClick?.(model)
        //     }
        //     handleClose={() => setShowApproveModal(false)}
        //     handleConfirm={() => {
        //       bulkOperations.approveSelectedAssignments();
        //       setShowApproveModal(false);
        //     }}
        //     missingExpenses={missingExpenses}
        //     numSelected={bulkOperations.selectedAssignmentIds.size}
        //   />
        // ))
      }
    </Wrapper>
  );
};

export default AutoApproveBulkApproval;
